/**
 * The terms of use and the privacy policy. Change LEGAL_UPDATED whenever either changes.
 *
 * Both pages are prerendered at build time (scripts/prerender.tsx), so crawlers that do not run
 * JavaScript, such as Google's OAuth verification, can read them.
 */
import { createContext, useContext } from 'react'
import { Link } from 'react-router'
import { ReadingPage } from '../components/ReadingPage'
import { useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'

const LEGAL_UPDATED = '29 October 2026'

/**
 * The address the pages name until the session says otherwise: the build's CONTACT_EMAIL, which
 * scripts/prerender.tsx writes into every page as <meta name="contact-email">. The prerender and
 * the app's first render both use it, so the address never changes as the page starts up.
 */
export const ContactEmail = createContext(
  typeof document === 'undefined'
    ? ''
    : (document.querySelector<HTMLMetaElement>('meta[name="contact-email"]')?.content ?? ''),
)

/** The server's CONTACT_EMAIL once the session has loaded, the build's before. */
function useContactEmail(): string {
  const fallback = useContext(ContactEmail)
  return useSession().contact_email ?? fallback
}

/** The server's CONTACT_EMAIL; a server that sets none is reached through its admins. */
function Mail() {
  const email = useContext(ContactEmail)
  return email ? <a href={`mailto:${email}`}>{email}</a> : <>this site’s admins</>
}

export function Terms() {
  useTitle('Terms of use')
  return (
    <ContactEmail.Provider value={useContactEmail()}>
      <TermsArticle />
    </ContactEmail.Provider>
  )
}

export function Privacy() {
  useTitle('Privacy policy')
  return (
    <ContactEmail.Provider value={useContactEmail()}>
      <PrivacyArticle />
    </ContactEmail.Provider>
  )
}

export function TermsArticle() {
  return (
    <ReadingPage>
      <h1 className="page-title">Terms of use</h1>
      <p className="lede">The rules for using GrimoireCodexDB and contributing to it. Last updated {LEGAL_UPDATED}.</p>

      <h2 id="service">The service</h2>
      <p>
        GrimoireCodexDB (“the database”, “we”) is a community-maintained catalogue of tabletop game metadata, run by its
        maintainers as a free, non-commercial project. You can read it, on the site or through the API, without an
        account. By using it you agree to these terms; if you do not agree, please do not use it.
      </p>

      <h2 id="accounts">Accounts</h2>
      <ul>
        <li>
          You sign in with Discord, GitHub or Google. You need to be at least 13, or the age of digital consent where
          you live if that is higher.
        </li>
        <li>
          One person per account. Keep your sign-in and API tokens to yourself; you are responsible for what is done
          with them.
        </li>
        <li>
          Moderators and admins can hide content, reject edits, change roles and suspend accounts that break these terms
          or the <Link to="/about#standards">editing guidelines</Link>. Every moderation decision is logged.
        </li>
      </ul>

      <h2 id="contributions">Your contributions</h2>
      <p>
        Records, edits, descriptions, notes, comments and votes you submit are published, attributed to your display
        name and kept in record history.
      </p>
      <ul>
        <li>
          <strong>Metadata is dedicated to the public domain.</strong> By submitting a contribution you release it under{' '}
          <a href="https://creativecommons.org/publicdomain/zero/1.0/" target="_blank" rel="noopener noreferrer">
            CC0 1.0
          </a>
          , so anyone, including Grimoire and other library managers, can reuse it for any purpose without asking. Only
          contribute what you are able to release this way: facts, and descriptions in your own words. The one exception
          is someone else’s writing that its author lets us show: add it only with their permission, and credit them
          under “Credited sources” on the record, which keeps it out of the CC0 release.
        </li>
        <li>
          <strong>Covers and portraits are not CC0.</strong> A cover belongs to its publisher and a portrait to its
          photographer. Upload only an official cover or promotional image, at the size needed to identify the product,
          or a portrait you took, own, or that its subject published for this kind of use. You give us permission to
          store, resize and show it on GrimoireCodexDB and through the API.
        </li>
        <li>
          <strong>Some text is credited to another catalogue and is not CC0.</strong> Some records carry a description
          or tags written by another catalogue or author, shown with their permission. The record says so under the
          description and links the source. Those fields are not part of the CC0 release: if you reuse them, credit the
          named source and link to it.
        </li>
        <li>Your contributions stay in history if you leave, because the records depend on them.</li>
      </ul>

      <h2 id="content">What does not belong here</h2>
      <ul>
        <li>
          Copyrighted content: rulebook text, PDFs, maps, tokens, stat lines or rules, or a publisher’s description
          pasted wholesale.
        </li>
        <li>Links to unofficial or pirated copies of anything.</li>
        <li>Spam, advertising, deliberately false information, or vandalism of records.</li>
        <li>Harassment, hate, or personal information about anyone beyond their public professional credits.</li>
        <li>Pretending to be a publisher or creator, including in an ownership claim.</li>
      </ul>
      <p>If you find something that breaks these rules, use the Report button on the record, or email us.</p>

      <h2 id="api">Using the API</h2>
      <ul>
        <li>
          The API is free to use. Data from it is CC0, except that covers and portraits keep their owners’ rights, and
          fields a record lists in <code>attributions</code> must be credited to the source named there.
        </li>
        <li>
          Please be gentle: cache what you can, and send a <code>User-Agent</code> that names your app and how to reach
          you. Clients that harm the service may be rate-limited or blocked.
        </li>
        <li>Edits made with an API token are yours, under the same rules as edits made on the site.</li>
      </ul>

      <h2 id="copyright">Copyright complaints</h2>
      <p>
        If you hold the rights to something on GrimoireCodexDB and want it removed, email <Mail /> with the page
        address, what you own, and how to contact you. We will remove infringing material promptly.
      </p>

      <h2 id="disclaimer">No warranty</h2>
      <p>
        GrimoireCodexDB is written by volunteers and provided “as is”, without any warranty. Information may be wrong or
        incomplete; check with the publisher before relying on it. To the extent the law allows, we are not liable for
        any loss arising from using GrimoireCodexDB. The service may change, pause or stop at any time.
      </p>

      <h2 id="changes">Changes and contact</h2>
      <p>
        We may update these terms; the date at the top shows the latest version, and significant changes will be
        announced on the site. Questions go to <Mail />. See also the <Link to="/privacy">privacy policy</Link>.
      </p>
    </ReadingPage>
  )
}

export function PrivacyArticle() {
  return (
    <ReadingPage>
      <h1 className="page-title">Privacy policy</h1>
      <p className="lede">
        What personal information GrimoireCodexDB collects, why, where it is kept, who it is shared with, and how to
        have it deleted. Last updated {LEGAL_UPDATED}.
      </p>

      <h2 id="who">Who we are</h2>
      <p>
        GrimoireCodexDB (db.grimoirecodex.org, “the database”, “we”) is a free, non-commercial, community-maintained
        catalogue of tabletop game metadata, run by its maintainers. Questions about this policy or your data go to{' '}
        <Mail />.
      </p>

      <h2 id="collect">What we collect</h2>
      <ul>
        <li>
          <strong>Nothing, if you only read.</strong> Browsing the site or the API needs no account, sets no cookies and
          stores nothing about you in our database.
        </li>
        <li>
          <strong>Account information</strong> from the sign-in provider you choose (Discord, GitHub or Google): that
          account’s id and username, your display name, your profile picture’s address, and your email address with
          whether the provider has verified it. We keep the email only if it is verified.
        </li>
        <li>
          <strong>What you contribute:</strong> records, edits, notes, comments, votes, reports, ownership claims and
          the cover images and portraits you upload.
        </li>
        <li>
          <strong>Support messages</strong> you send from the <Link to="/support">support page</Link>: your name, the
          email address you give for our reply, and your message, linked to your account if you are signed in. To limit
          spam we also keep a one-way hash of your IP address for an hour.
        </li>
        <li>
          <strong>Technical data:</strong> a sign-in cookie, and request logs (IP address, page requested, time).
        </li>
      </ul>
      <p>
        We do not collect payment details, your location, your contacts, or anything else from your sign-in account.
      </p>

      <h2 id="google">Google user data</h2>
      <p>
        GrimoireCodexDB uses Google only for sign-in. When you choose “Continue with Google”, it asks Google for the{' '}
        <code>openid</code>, <code>email</code> and <code>profile</code> scopes and nothing else.
      </p>
      <ul>
        <li>
          <strong>Data accessed:</strong> your Google account id, your name, your profile picture’s address, your email
          address, and whether Google has verified that email. We do not access your Gmail, Drive, Calendar, contacts or
          any other Google service or data.
        </li>
        <li>
          <strong>Data usage:</strong> to create your GrimoireCodexDB account and sign you in; to show your name and
          picture next to your contributions; to recognise you when you also sign in with Discord or GitHub using the
          same verified email; and to contact you about your account, ownership claims or moderation decisions. We use
          it for nothing else: not for advertising, not to build profiles, and not to develop, improve or train AI or
          machine-learning models.
        </li>
        <li>
          <strong>Data sharing:</strong> we do not sell, rent or share your Google user data with anyone. The only other
          party that handles it is Cloudflare, which hosts our database and processes it on our behalf. We would
          disclose it only if the law required us to.
        </li>
        <li>
          <strong>Data storage and protection:</strong> it is stored in our database, hosted by Cloudflare and reached
          only over HTTPS. Your email is never shown publicly. Only GrimoireCodexDB’s maintainers can access the
          database, and they look at personal data only when needed to run the service, for example to answer your
          request or investigate abuse.
        </li>
        <li>
          <strong>Data retention and deletion:</strong> we keep it for as long as you have an account. Delete your
          account at any time from your <Link to="/account">account page</Link>, which removes your Google user data
          from our database at once, or email <Mail /> from your account’s address and we delete it within 30 days.
          Database backups that still hold it expire within 30 days.
        </li>
        <li>
          <strong>Revoking access:</strong> you can remove GrimoireCodexDB’s access at any time at{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            myaccount.google.com/permissions
          </a>
          . That stops future sign-ins with Google; to delete what we already hold, delete your account or email{' '}
          <Mail />.
        </li>
        <li>
          <strong>Changes:</strong> if we ever change how we use Google user data, we will update this policy first and
          tell everyone who signs in with Google, on the site and by email, before the change applies.
        </li>
      </ul>
      <p>
        GrimoireCodexDB’s use and transfer of information received from Google APIs adheres to the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Information from Discord and GitHub is handled the same way.
      </p>

      <h2 id="use">How we use information</h2>
      <ul>
        <li>To run your account and keep you signed in.</li>
        <li>
          To publish your contributions. Your edits, votes and comments are public and attributed to your display name;
          that is how review works.
        </li>
        <li>
          To moderate: ownership claims, reports and uploads are linked to your account so moderators can review them.
          The evidence you give for a claim is seen only by admins.
        </li>
        <li>
          To answer support messages. Only admins read them, and the email address you give is used only to reply.
        </li>
        <li>To keep the service working and secure, and to stop spam and abuse.</li>
      </ul>
      <p>We do not use your information for advertising or profiling, and we never sell it.</p>

      <h2 id="grimoire">Grimoire and other library managers</h2>
      <p>
        Grimoire’s GrimoireCodexDB integration is off unless the server’s admin turns it on, and it can run in
        lookup-only mode. When it looks a file up it may send:
      </p>
      <ul>
        <li>the file’s hashes (SHA-256 and similar), which identify a file without revealing anything in it;</li>
        <li>its ISBN, product code, title, file name, author and publisher names, system name, year and page count.</li>
      </ul>
      <p>
        It never sends the file, its pages or its text. Lookups are not stored. If you submit a book from Grimoire, what
        you submit becomes an ordinary public edit attributed to your GrimoireCodexDB account, including any file hashes
        you chose to include.
      </p>
      <p>
        A hash says “someone has this exact file”. When you submit one, that link between your account and the file is
        public in the edit history. Leave hashes out if that matters to you.
      </p>

      <h2 id="sharing">Who else is involved</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> hosts GrimoireCodexDB, its database and its images, handles every request on our
          behalf, and delivers the email that passes a support message on to the admins. Its network is worldwide, so
          your data may be processed outside your country.
        </li>
        <li>
          <strong>Discord, GitHub and Google</strong> handle sign-in under their own privacy policies. Profile pictures
          are shown from their image servers, so viewing one means your browser fetches it from them.
        </li>
      </ul>
      <p>
        There are no analytics, advertising or tracking scripts. We share personal information with no one else, unless
        the law requires us to.
      </p>

      <h2 id="cookies">Cookies and local storage</h2>
      <p>
        Signing in uses a few short-lived cookies to complete the sign-in safely, then one cookie keeps you signed in
        for 30 days. They are needed for the site to work and are never used for tracking. Your browser also keeps your
        theme choice in its own local storage, on your device only.
      </p>

      <h2 id="retention">How long we keep it</h2>
      <ul>
        <li>
          Account information: until you delete your account, or ask us to, then removed from our database at once and
          from backups within 30 days.
        </li>
        <li>Sign-in sessions: 30 days, or until you sign out.</li>
        <li>Request logs: a few days.</li>
        <li>Support messages: until they are dealt with, then deleted a year after they are closed.</li>
        <li>
          Contributions: they stay in record history, because the records depend on them. Once your account is deleted
          they are attributed to a deleted user.
        </li>
      </ul>

      <h2 id="security">Security</h2>
      <p>
        Every connection uses HTTPS. Sign-in sessions and API tokens are stored only as one-way hashes, so a copy of the
        database cannot be used to sign in as you. Access to the database is limited to GrimoireCodexDB’s maintainers.
      </p>

      <h2 id="deletion">Your choices and rights</h2>
      <ul>
        <li>Change your display name, revoke API tokens, and delete your account, on your account page.</li>
        <li>
          Email <Mail /> from your account’s address to get a copy of your data, correct it, object to how it is used,
          or have your account deleted for you. We reply within 30 days.
        </li>
        <li>Revoke GrimoireCodexDB’s access from your Discord, GitHub or Google account settings at any time.</li>
      </ul>

      <h2 id="children">Children</h2>
      <p>GrimoireCodexDB is not meant for children under 13, and we do not knowingly keep their data.</p>

      <h2 id="changes">Changes and contact</h2>
      <p>
        If this policy changes, the date at the top changes too, and significant changes are announced on the site.
        Questions go to <Mail />. See also the <Link to="/terms">terms of use</Link>.
      </p>
    </ReadingPage>
  )
}
